import React from "react";
import { ExternalLink, Facebook, Instagram, Youtube, Heart, MessageCircle, Play, Eye } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { mockSocialMediaFeeds } from "../mockData";

const SocialMedia = () => {
  const socialLinks = [
    {
      platform: "Facebook",
      url: "https://www.facebook.com/p/Football-X-Academy-100053262415608/",
      icon: <Facebook className="w-8 h-8" />,
      description: "Follow our daily updates, match results, and community highlights",
      color: "hover:text-blue-600"
    },
    {
      platform: "Instagram",
      url: "https://instagram.com/football_x_academy",
      icon: <Instagram className="w-8 h-8" />,
      description: "Photos from training sessions, behind-the-scenes content, and player spotlights",
      color: "hover:text-pink-600"
    },
    {
      platform: "YouTube",
      url: "https://youtube.com/@footballx-media",
      icon: <Youtube className="w-8 h-8" />,
      description: "Football X Media podcasts, training videos, and match highlights",
      color: "hover:text-red-600"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-black text-white py-32 overflow-hidden min-h-screen flex items-center">
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
          >
            <source
              src="https://customer-assets.emergentagent.com/job_football-academy-x/artifacts/lkqvovbe_intro-maker-featuring-a-colorful-logo-reveal-1749-el1.MP4"
              type="video/mp4"
            />
            Your browser does not support the video tag.
          </video>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center pt-8">
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              STAY <span className="text-cyan-400">CONNECTED</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Follow Football X across all our social media platforms for the latest updates, 
              exclusive content, and behind-the-scenes access to our programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold">
                Follow All Platforms
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Platforms */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              OUR SOCIAL <span className="text-cyan-400">PLATFORMS</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connect with us across multiple platforms to get the full Football X experience. 
              Each platform offers unique content and perspectives on our football community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {socialLinks.map((social, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:border-cyan-400">
                <CardContent className="p-8 text-center">
                  <div className={`text-gray-600 ${social.color} transition-colors duration-300 mb-6`}>
                    {social.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-black mb-4">{social.platform}</h3>
                  <p className="text-gray-600 mb-6">{social.description}</p>
                  <Button 
                    asChild 
                    className="w-full bg-black hover:bg-gray-800"
                  >
                    <a 
                      href={social.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center"
                    >
                      Follow Us <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Xyro's Goals & Saves */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-6">
              XYRO'S GOALS & <span className="text-cyan-400">SAVES OF THE WEEK</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watch the best goals and saves from our players every week.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <a href="https://www.youtube.com/watch?v=rgHuGYW9rHg" target="_blank" rel="noopener noreferrer" className="group">
              <Card className="bg-gray-900 border-gray-700 hover:border-cyan-400 transition-all duration-300 overflow-hidden">
                <div className="relative aspect-video">
                  <img 
                    src="https://img.youtube.com/vi/rgHuGYW9rHg/maxresdefault.jpg"
                    alt="Xyro's Goals Of The Week 10/05/26"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                    <Play className="w-14 h-14 text-cyan-400" />
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white">Xyro's Goals Of The Week</h3>
                  <p className="text-cyan-400 text-sm mt-1">10/05/26</p>
                </CardContent>
              </Card>
            </a>

            <a href="https://www.youtube.com/watch?v=HNMDXVfO_rQ" target="_blank" rel="noopener noreferrer" className="group">
              <Card className="bg-gray-900 border-gray-700 hover:border-cyan-400 transition-all duration-300 overflow-hidden">
                <div className="relative aspect-video">
                  <img 
                    src="https://img.youtube.com/vi/HNMDXVfO_rQ/maxresdefault.jpg"
                    alt="Xyro Goals and Saves of the Week 17/05/26"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                    <Play className="w-14 h-14 text-cyan-400" />
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white">Xyro Goals and Saves of the Week</h3>
                  <p className="text-cyan-400 text-sm mt-1">17/05/26</p>
                </CardContent>
              </Card>
            </a>

            <a href="https://www.youtube.com/watch?v=p4BBYCRqA8M" target="_blank" rel="noopener noreferrer" className="group">
              <Card className="bg-gray-900 border-gray-700 hover:border-cyan-400 transition-all duration-300 overflow-hidden">
                <div className="relative aspect-video">
                  <img 
                    src="https://img.youtube.com/vi/p4BBYCRqA8M/maxresdefault.jpg"
                    alt="Xyro's Goals and Saves of the Week 24/05/26"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                    <Play className="w-14 h-14 text-cyan-400" />
                  </div>
                </div>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-white">Xyro's Goals and Saves of the Week</h3>
                  <p className="text-cyan-400 text-sm mt-1">24/05/26</p>
                </CardContent>
              </Card>
            </a>
          </div>

          <div className="text-center mt-12">
            <Button 
              asChild 
              size="lg" 
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-semibold"
            >
              <a 
                href="https://youtube.com/@footballx-media" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Visit Our YouTube Channel <ExternalLink className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Social Media Feeds */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-black mb-6">
              RECENT <span className="text-cyan-400">UPDATES</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay up to date with our latest posts, photos, and community highlights 
              from across all our social media platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Facebook Feed */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Facebook className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-black">Facebook Updates</h3>
              </div>
              
              <div className="space-y-6">
                {mockSocialMediaFeeds.facebook.posts.map((post, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <p className="text-gray-700 mb-4">{post.content}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{post.date}</span>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1">
                            <Heart className="w-4 h-4" />
                            <span>{post.likes}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <MessageCircle className="w-4 h-4" />
                            <span>{post.comments}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Instagram Feed */}
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <Instagram className="w-8 h-8 text-pink-600" />
                <h3 className="text-2xl font-bold text-black">Instagram Photos</h3>
              </div>
              
              <div className="space-y-6">
                {mockSocialMediaFeeds.instagram.posts.map((post, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <div className="aspect-square">
                      <img 
                        src={post.image} 
                        alt="Instagram post"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-4">
                      <p className="text-gray-700 mb-3">{post.caption}</p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{post.date}</span>
                        <div className="flex items-center space-x-1">
                          <Heart className="w-4 h-4" />
                          <span>{post.likes} likes</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-cyan-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            JOIN THE FOOTBALL X COMMUNITY
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-2xl mx-auto">
            Don't miss out on exclusive content, training tips, and community updates. 
            Follow us on all platforms and be part of the Football X family.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              size="lg" 
              className="bg-black hover:bg-gray-800 text-white"
            >
              <a href="https://www.facebook.com/p/Football-X-Academy-100053262415608/" target="_blank" rel="noopener noreferrer">
                Follow on Facebook
              </a>
            </Button>
            <Button 
              asChild 
              size="lg" 
              className="bg-black hover:bg-gray-800 text-white"
            >
              <a href="https://instagram.com/football_x_academy" target="_blank" rel="noopener noreferrer">
                Follow on Instagram
              </a>
            </Button>
            <Button 
              asChild 
              size="lg" 
              className="bg-black hover:bg-gray-800 text-white"
            >
              <a href="https://youtube.com/@footballx-media" target="_blank" rel="noopener noreferrer">
                Subscribe to YouTube
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMedia;